import React from "react";
import { render } from "react-dom";
import { PlayerContainerComponent } from "../../components/player/PlayerContainerComponent";
import { UnsupportedBrowserVersionComponent } from "../../components/player/UnsupportedBrowserVersionComponent";
import { createApplicationContext, createNotSupportedBrowserApplicationContext } from "../../manager/di/applicationContext";
import { TYPES } from "../../manager/di/types";
import { SupportedBrowserHelper } from "../../utils/SupportedBrowserHelper";
import { getI18n } from "../../utils/translations";
import { VintomError } from "../model/error/VintomError";
import { PlayerActionState } from "../model/playerState/PlayerActionState";
import { PlayerState } from "../model/playerState/PlayerState";
import { PlayerConfigurationAPI, PlayerDimensionsAPI, PlayerExecutionAPI, PlayerPersonalizationAPI, RunningPlayerAPI } from "./PlayerAPI";
import { createPlayerSetupConfig } from "./PlayerConfigTypes";
import { PlayerManager } from "../../manager/playerManager/PlayerManager";
import { createRoot } from "react-dom/client";

// TODO - I feel like this class should be split into builder / runner
export class PlayerAPIImpl implements PlayerConfigurationAPI, PlayerPersonalizationAPI, PlayerDimensionsAPI, PlayerExecutionAPI {
  private playerComponent: PlayerContainerComponent;
  private playerManager: PlayerManager;

  private configurationJSON: any;
  private personalizationJSON: any;

  private width: number;
  private height: number;

  constructor(
    private elementId: string,
    private videoCode: string,
  ) {}

  setup(json: any): PlayerPersonalizationAPI & PlayerDimensionsAPI & PlayerExecutionAPI {
    this.configurationJSON = json;
    return this;
  }

  personalize(json: any): PlayerDimensionsAPI & PlayerExecutionAPI {
    this.personalizationJSON = json;
    return this;
  }

  dimensions(width: number, height: number): PlayerExecutionAPI {
    this.width = width;
    this.height = height;
    return this;
  }

  run(): RunningPlayerAPI {
    if (SupportedBrowserHelper.isBrowserSupported()) {
      this.runForSupportedBrowser();
    } else {
      this.runForUnsupportedBrowser();
    }

    return this;
  }

  addVolumeTransition(videoTime: number, targetVolume: number, duration: number): void {
    const musicManager = this.playerManager?.getMusicManager();

    musicManager?.addVolumeTransition(videoTime, targetVolume, duration);
  }

  addVolumeTransitionAtCurrentTime(targetVolume: number, duration: number): void {
    const musicManager = this.playerManager?.getMusicManager();

    musicManager?.addVolumeTransition(musicManager?.currentTime, targetVolume, duration);
  }

  play(): void {
    this.playerManager && this.playerManager.play();
  }

  pause(): void {
    this.playerManager && this.playerManager.pause();
  }

  move(id: string): void {
    this.playerManager && this.playerManager.moveToScenarioElement(id);
  }

  replay(): void {
    this.playerManager && this.playerManager.replay();
  }

  action(ctaId: number, actionId: number, ...args: any[]): void {
    this.playerManager && this.playerManager.performAction(ctaId, actionId, args);
  }

  on(event: "ready", callback: () => void): void;
  on(event: "error", callback: (error: VintomError) => void): void;
  on(event: "play" | "pause" | "finish", callback: (payload: PlayerState) => void): void;
  on(event: "action", callback: (payload: PlayerActionState) => void): void;
  on(event: "ready" | "error" | "play" | "pause" | "finish" | "action", callback: (payload: any) => void): void {
    switch (event) {
      case "ready":
        this.vintomPlayerReady(callback as () => void);
        break;
      case "error":
        this.vintomPlayerError(callback as (error: VintomError) => void);
        break;
      case "play":
        this.vintomPlayerPlay(callback as (payload: PlayerState) => void);
        break;
      case "pause":
        this.vintomPlayerPause(callback as (payload: PlayerState) => void);
        break;
      case "finish":
        this.vintomPlayerFinish(callback as (payload: PlayerState) => void);
        break;
      case "action":
        this.vintomPlayerAction(callback as (payload: PlayerActionState) => void);
        break;
      default:
        break;
    }
  }

  vintomPlayerReady(callback: () => void): void {
    this.playerManager && this.playerManager.registerOnReady(callback);
  }

  vintomPlayerError(callback: (error: VintomError) => void): void {
    this.playerManager && this.playerManager.registerOnError(callback);
  }

  vintomPlayerPlay(callback: (payload: PlayerState) => void): void {
    this.playerManager && this.playerManager.registerOnPlay(callback);
  }

  vintomPlayerPause(callback: (payload: PlayerState) => void): void {
    this.playerManager && this.playerManager.registerOnPause(callback);
  }

  vintomPlayerFinish(callback: (payload: PlayerState) => void): void {
    this.playerManager && this.playerManager.registerOnFinish(callback);
  }

  vintomPlayerAction(callback: (payload: PlayerActionState) => void): void {
    this.playerManager && this.playerManager.registerOnAction(callback);
  }

  getState(): PlayerState {
    return this.playerManager && this.playerManager.getState();
  }

  remove(): void {
    this.playerManager && this.playerManager.prepareForRemove();
    document.getElementById(this.elementId).innerHTML = "";
  }

  private runForSupportedBrowser() {
    const root = createRoot(document.getElementById(this.elementId) as HTMLElement);

    const initialize = (element: PlayerContainerComponent) => {
      const applicationContext = createApplicationContext(
        element,
        element.animationLayer,
        element.controlsLayer,
        element.ctaLayer,
        element.posterLayer,
        element.videoLayer,
        element.playbackInformation,
      );

      this.playerManager = applicationContext.get<PlayerManager>(TYPES.PlayerManager);

      this.playerManager.initialize(this.createSetupConfig()).then(() => {
        this.playerManager.run();
      }).catch(e => console.error(e));
    };

    root.render(<PlayerContainerComponent ref={initialize} i18n={getI18n()} width={this.width} height={this.height} />);
  }

  private runForUnsupportedBrowser() {
    const context = createNotSupportedBrowserApplicationContext();
    const root = createRoot(document.getElementById(this.elementId) as HTMLElement);

    root.render(<UnsupportedBrowserVersionComponent i18n={getI18n()} width={this.width} height={this.height} />);

    this.playerManager = context.get<PlayerManager>(TYPES.PlayerManager);

    this.playerManager.initialize(this.createSetupConfig()).then(() => {
      this.playerManager.run();
    }).catch(e => console.error(e));
  }

  private createSetupConfig() {
    return createPlayerSetupConfig(this.videoCode, this.configurationJSON, this.personalizationJSON);
  }
}
